import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Icon, Checkbox, Tag } from "@src/components/atoms";
import useOutsideClick from "@src/components/utility/OutsideClickHook";
import AppUtils from "@src/utils/appUtils";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";

const MultiSelect = (props) => {
  const [open, setOpen] = useState(false);
  const [searchField, setSearchField] = useState("");
  const [longestOptionLength, setLongestOptionLength] = useState(0);
  const {
    selected,
    options,
    defaultText = "All",
    searchText = "Search options",
    multiSelectText = "selected options",
    className,
    minWidth = true,
    onChange,
  } = props;

  //work out length of longest option so we can set the width of the dropdown to accomodate it when it isnt the selected option
  useEffect(() => {
    // use the previous value so the dropdown doesnt get smaller when the options change
    let result = longestOptionLength;
    options.forEach((option) => {
      result = option.text?.length > result ? option.text?.length : result;
    });
    setLongestOptionLength(result);
  }, [options]);

  const handleClickOutside = () => {
    setOpen(false);
  };

  const ref = useOutsideClick(handleClickOutside);

  const valueToDisplay =
    selected.length === 0
      ? defaultText
      : selected.length === 1
        ? selected[0].text
        : `${selected.length} ${multiSelectText}`;

  const handleOptionSelect = (option) => {
    let selectedOptions = [...selected];
    if (
      selectedOptions.filter(
        (selectedOption) => selectedOption.value === option.value,
      ).length > 0
    ) {
      selectedOptions = selectedOptions.filter(
        (selectedOption) => selectedOption.value !== option.value,
      );
    } else {
      selectedOptions.push(option);
    }
    onChange(selectedOptions);
  };

  const handleUnselectAll = () => {
    onChange([]);
  };

  let optionsToDisplay = [];

  if (searchField.length > 0) {
    optionsToDisplay = options.filter((option) => {
      const optionText = option.text.toLowerCase();
      const searchTerm = searchField.toLowerCase();
      return optionText.includes(searchTerm);
    });
  } else {
    optionsToDisplay = [...options];
  }

  return (
    <div
      ref={ref}
      className={`h-10${minWidth ? " min-w-64" : ""} relative ${className}`}
    >
      <div
        onClick={() => setOpen(!open)}
        className={`flex h-full items-center justify-between px-3 cursor-pointer border-1 border-neutral-200 rounded-md bg-white ${
          open ? "border-b-0 rounded-b-none" : ""
        }`}
      >
        <span
          className="mr-2"
          style={
            longestOptionLength && longestOptionLength > 0
              ? { minWidth: `${longestOptionLength * 7.5}px` }
              : {}
          }
        >
          {valueToDisplay}
        </span>
        <Icon
          name="DropDownChevron"
          backgroundColor="white"
          size="4"
          padding={false}
          centered={false}
          className={`transition-all ${open ? "transform rotate-180" : ""}`}
        />
      </div>
      <div
        className={`flex flex-col items-center absolute z-999 w-full bg-white rounded-b transition-all ${
          open ? "border-1 border-neutral-200 border-t-0 pb-2" : ""
        }`}
      >
        <div
          className={open ? "my-2 border-b-2 border-b-neutral-200 w-5/6" : ""}
        ></div>
        <div
          className={`w-full transition-all overflow-hidden flex flex-col`}
          style={{
            height: open ? (options.length > 4 ? "20rem" : `auto`) : "0rem",
          }}
        >
          {selected.length > 0 && (
            <div className="flex flex-wrap pt-4 px-4">
              {selected.map((option, index) => (
                <Tag
                  key={`selectedOptionTag${index}`}
                  className="my-2"
                  closeFunc={() => handleOptionSelect(option)}
                >
                  {option.text}
                </Tag>
              ))}
            </div>
          )}
          <button
            className="w-full flex items-start p-4 whitespace-nowrap text-base font-bold"
            onClick={handleUnselectAll}
          >
            {defaultText}
          </button>
          {options.length > 4 && (
            <div className="w-full px-2">
              <input
                className={`text-sm appearance-none block w-full bg-white text-gray-700 placeholder-gray-400 border rounded-lg py-3 px-4 leading-tight focus:outline-none
               border-gray-200 hover:border-gray-400 
              focus:bg-white focus:border-primary focus:border-primary-500 
              data-hj-allow`}
                placeholder={searchText}
                value={searchField}
                name="search"
                onChange={(e) => setSearchField(e.target.value)}
              />
            </div>
          )}
          <div
            className={`w-full flex flex-col ${
              optionsToDisplay.length >= 3
                ? "overflow-y-scroll"
                : "overflow-y-hidden"
            }`}
          >
            {optionsToDisplay.map((option, index) => (
              <div
                key={`multiSelectOption${index}`}
                className="w-full flex items-center px-4"
                onClick={() => handleOptionSelect(option)}
              >
                <span className="pointer-events-none">
                  <Checkbox
                    checked={
                      selected.filter(
                        (selectedOption) =>
                          selectedOption.value === option.value,
                      ).length > 0
                    }
                    name={option.text}
                    onChange={() => {}}
                  />
                </span>
                <span className="whitespace-nowrap text-base font-medium">
                  {option.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const multiSelectControls = {
  selected: {
    propType: PropTypes.array,
    description: "Array of currently selected options",
    controlType: "object",
    defaultValue: [],
  },
  options: {
    propType: PropTypes.array,
    description: "Available options array",
    controlType: "object",
    defaultValue: [
      { value: "opt1", label: "Option 1" },
      { value: "opt2", label: "Option 2" },
      { value: "opt3", label: "Option 3" },
    ],
  },
  defaultText: {
    propType: PropTypes.string,
    description: "Placeholder text when no options are selected",
    controlType: "text",
    defaultValue: "Select options...",
  },
  searchText: {
    propType: PropTypes.string,
    description: "Placeholder text for search input",
    controlType: "text",
    defaultValue: "Search...",
  },
  multiSelectText: {
    propType: PropTypes.string,
    description: "Text to display when multiple items are selected",
    controlType: "text",
    defaultValue: "{count} items selected",
  },
  className: COMMON_CONTROLS.className,
  minWidth: {
    propType: PropTypes.bool,
    description: "Enable minimum width constraint",
    controlType: "boolean",
    defaultValue: true,
  },
  onChange: {
    propType: PropTypes.func,
    description: "Callback when selection changes",
    disable: true,
  },
};

MultiSelect.propTypes = AppUtils.getPropTypeFromControls(multiSelectControls);

export default MultiSelect;
